<template>
  <el-tooltip
    effect="light"
    popper-class="light-tooltip-popper ai-resume-match-tooltip"
    :placement="placement"
    :disabled="hiddenTip || !hasTooltipContent"
  >
    <template #content>
      <div v-if="hmDecisionList.length" class="hm-decision-list">
        <div v-if="!hiddenCompletion" class="hm-completion">{{ $t(`${i18nPrefix}.label.ratingCompletionNumber`, {completion: completion || 0}) }}</div>
        <div v-for="(hmInfo, index) in hmDecisionList" :key="hmInfo.id == null ? index : hmInfo.id">
          {{ hmInfo.fullName }}: {{ hmInfo.decisionName }}
        </div>
      </div>
      <span v-else>{{ decisionName }}</span>
    </template>
    <div class="decision-result-chart fit-score" ref="chart" :style="fitScoreStyle">{{ '-' }}</div>
  </el-tooltip>
</template>

<script>
import _ from 'lodash'
import * as echarts from 'echarts'
import { generateFitEchartsOption } from '@/constant/application'
import { HM_RECOMMENDATION_DISPLAY_MAP } from '@/constant/questionnaire'

const RING_GRAY_COLOR = '#c3c5c9'

export default {
  props: {
    decision: {
      required: false
    },
    score: {
      required: false
    },
    completion: {
      type: [Number, String],
      default: null
    },
    hmInfoList: {
      type: Array,
      default: () => []
    },
    fitScoreStyle: {
      type: Object,
      required: true
    },
    ringBackgroundColor: {
      type: String,
      default: '#ebeef5'
    },
    circleWidth: {
      type: Number,
      default: 5
    },
    fontSize: {
      type: [Number, String],
      default: '18px'
    },
    hiddenTip: {
      type: Boolean,
      default: false
    },
    placement: {
      type: String,
      default: 'bottom'
    },
    chartOption: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    decisionConfig () {
      return HM_RECOMMENDATION_DISPLAY_MAP[this.decision]
    },
    decisionName () {
      return this.getDecisionName(this.decision)
    },
    hmDecisionList () {
      return _.map(this.hmInfoList, hmInfo => ({
        id: hmInfo.id,
        fullName: hmInfo.full_name,
        decisionName: this.getDecisionName(hmInfo.rating_decision || hmInfo.interview_decision)
      }))
    },
    hasTooltipContent () {
      return !_.isEmpty(this.hmDecisionList) || Boolean(this.decisionName)
    },
    hiddenCompletion () {
      return _.includes([null, undefined, ''], this.completion) ||
        (this.completion === 100 && _.includes([null, undefined, ''], this.score))
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate'
    }
  },
  async mounted () {
    await this.$nextTick()
    this.refreshChart()
  },
  beforeDestroy () {
    this.disposeChart()
  },
  methods: {
    getDecisionName (decision) {
      const config = HM_RECOMMENDATION_DISPLAY_MAP[decision]
      return config
        ? this.$t(`admin.application.candidate.label.${config.i18nKey}`)
        : _.toString(decision)
    },
    disposeChart () {
      const chart = this.$refs.chart && echarts.getInstanceByDom(this.$refs.chart)
      if (chart) {
        chart.dispose()
      }
    },
    refreshChart () {
      const chartElement = this.$refs.chart
      if (!chartElement) {
        return
      }

      const hasScore = ![null, undefined, ''].includes(this.score) && Number.isFinite(Number(this.score))
      const roundedScore = hasScore ? _.round(Number(this.score)) : null
      const hasCompletion = ![null, undefined, ''].includes(this.completion)
      const numericCompletion = hasCompletion ? Number(this.completion) : null
      const normalizedCompletion = Number.isFinite(numericCompletion) ? numericCompletion : null

      if (!this.decisionConfig && roundedScore === null) {
        this.disposeChart()
        chartElement.textContent = '-'
        return
      }

      const displayValue = roundedScore === null ? (this.decisionConfig && this.decisionConfig.shortLabel) || '···' : roundedScore
      const chartValue = normalizedCompletion === null ? 0 : normalizedCompletion
      const ringColor = (this.decisionConfig && this.decisionConfig.color) || RING_GRAY_COLOR
      const option = _.merge(
        {},
        generateFitEchartsOption(displayValue, chartValue, ringColor, this.fontSize, '#606266', this.ringBackgroundColor, this.circleWidth),
        this.chartOption
      )
      if (chartValue === 0) {
        option.series.progress.show = false
      }
      const chart = echarts.getInstanceByDom(chartElement) || echarts.init(chartElement)
      chart.setOption(option)
      this.$nextTick(() => {
        this.$emit('refresh-chart')
      })
    }
  },
  watch: {
    decision () {
      this.$nextTick(this.refreshChart)
    },
    score () {
      this.$nextTick(this.refreshChart)
    },
    completion () {
      this.$nextTick(this.refreshChart)
    },
    ringBackgroundColor () {
      this.$nextTick(this.refreshChart)
    },
    circleWidth () {
      this.$nextTick(this.refreshChart)
    },
    fontSize () {
      this.$nextTick(this.refreshChart)
    },
    chartOption: {
      deep: true,
      handler () {
        this.$nextTick(this.refreshChart)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.decision-result-chart {
  border-radius: 50%;
}

.hm-decision-list {
  line-height: 20px;
}
</style>
